import { useState, useEffect } from "react";
import { puedeCrear } from "../auth/permisos";
import DateInput from "../../shared/components/DateInput";

import { crear, directorioMiembros, eliminar, listar, actualizar, unir } from "../../services/datos";
import { avisar, confirmar } from "../../shared/avisos";
import Selector from "../../shared/components/Selector";
import Esqueleto from "../../shared/components/Esqueleto";

const fallo = (e) => avisar(e.message, "error");

function Cursos({ usuario }) {
  const [cursos, setCursos] = useState([]);
  const [ediciones, setEdiciones] = useState([]);
  const [miembros, setMiembros] = useState([]);
  const [edicionSeleccionada, setEdicionSeleccionada] = useState(null);
  const [inscripciones, setInscripciones] = useState([]);
  const [mostrarNuevaEdicion, setMostrarNuevaEdicion] = useState(false);
  const [mostrarInscribir, setMostrarInscribir] = useState(false);
  const [miembroAInscribir, setMiembroAInscribir] = useState("");
  const [busquedaInscripcion, setBusquedaInscripcion] = useState("");
  const [cargando, setCargando] = useState(false);
  const [nuevaEdicion, setNuevaEdicion] = useState({
    curso_id: "", fecha_inicio: "", fecha_fin: "", estado: "abierto", notas: ""
  });

  const cargarDatos = () => {
    listar("cursos", { orden: "nivel" }).then(setCursos).catch(fallo);
    Promise.all([
      listar("ediciones_curso", { select: "*, cursos(nombre, nivel)", orden: "fecha_inicio", descendente: true }),
      directorioMiembros(),
    ])
      .then(([lista, personas]) => {
        setMiembros(personas);
        setEdiciones(unir(lista, "dirigido_por_id", personas, "miembros"));
      })
      .catch(fallo);
  };

  useEffect(() => { cargarDatos(); }, []);

  const cargarInscripciones = (edicion) => {
    setEdicionSeleccionada(edicion);
    setCargando(true);
    Promise.all([listar("inscripciones_curso", { filtros: { edicion_id: edicion.id } }), directorioMiembros()])
      .then(([data, personas]) => { setInscripciones(unir(data, "miembro_id", personas, "miembros")); setCargando(false); })
      .catch((e) => { setCargando(false); fallo(e); });
  };

  const crearEdicion = () => {
    if (!nuevaEdicion.curso_id || !nuevaEdicion.fecha_inicio) {
      avisar("Curso y fecha de inicio son obligatorios");
      return;
    }
    crear("ediciones_curso", { ...nuevaEdicion, curso_id: parseInt(nuevaEdicion.curso_id) })
      .then(() => {
        setMostrarNuevaEdicion(false);
        setNuevaEdicion({ curso_id: "", fecha_inicio: "", fecha_fin: "", estado: "abierto", notas: "" });
        cargarDatos();
      })
      .catch(fallo);
  };

  const inscribirMiembro = () => {
    if (!miembroAInscribir) { avisar("Selecciona un miembro"); return; }
    const yaInscrito = inscripciones.find(i => i.miembro_id === parseInt(miembroAInscribir));
    if (yaInscrito) { avisar("Este miembro ya está inscrito"); return; }
    crear("inscripciones_curso", { edicion_id: edicionSeleccionada.id, miembro_id: parseInt(miembroAInscribir) })
      .then(() => {
        setMostrarInscribir(false);
        setMiembroAInscribir("");
        setBusquedaInscripcion("");
        cargarInscripciones(edicionSeleccionada);
      })
      .catch(fallo);
  };

  const toggleCompletado = (inscripcion) => {
    const completado = !inscripcion.completado;
    actualizar("inscripciones_curso", inscripcion.id, {
        completado,
        fecha_completado: completado ? new Date().toLocaleDateString("en-CA") : null
      })
      .then(() => cargarInscripciones(edicionSeleccionada))
      .catch(fallo);
  };

  const eliminarInscripcion = async (id, nombre) => {
    if ((await confirmar(`¿Quitar a ${nombre} del curso?`, { aceptar: "Quitar" }))) {
      eliminar("inscripciones_curso", id)
        .then(() => cargarInscripciones(edicionSeleccionada))
        .catch(fallo);
    }
  };

  const cambiarEstadoEdicion = (edicion, nuevoEstado) => {
    actualizar("ediciones_curso", edicion.id, { estado: nuevoEstado })
      .then(() => {
        cargarDatos();
        if (edicionSeleccionada?.id === edicion.id) {
          setEdicionSeleccionada({ ...edicion, estado: nuevoEstado });
        }
      })
      .catch(fallo);
  };

  const colorEstado = {
    abierto: "border border-emerald-500/30 bg-emerald-500/15 text-emerald-300",
    en_curso: "border border-amber-500/30 bg-amber-500/15 text-amber-300",
    finalizado: "border border-zinc-500/30 bg-zinc-500/15 text-zinc-400",
  };

  const input = "campo";
  const label = "etiqueta";

  const totalCompletados = inscripciones.filter(i => i.completado).length;
  const idsInscritos = new Set(inscripciones.map(i => i.miembro_id));
  const miembroSeleccionado = miembros.find(m => m.id === parseInt(miembroAInscribir));
  const busquedaLimpia = busquedaInscripcion.trim().toLowerCase();
  const miembrosDisponibles = miembros
    .filter(m => !idsInscritos.has(m.id))
    .filter(m => {
      if (!busquedaLimpia) return false;
      const nombreCompleto = `${m.nombres} ${m.apellidos}`.toLowerCase();
      return nombreCompleto.includes(busquedaLimpia);
    })
    .slice(0, 8);

  return (
    <div className="p-6">

      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="titulo-pagina">Cursos</h2>
          <p className="text-gray-400 text-sm mt-1">Gestión de cursos y formación</p>
        </div>
        {puedeCrear(usuario) ? (
          <button
            onClick={() => setMostrarNuevaEdicion(true)}
            className="btn-primario"
          >
            Nueva Edición
          </button>
        ) : (
          <span className="text-sm text-gray-400">Solo pastor o administrador puede crear</span>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Lista de ediciones */}
        <div className="panel p-4">
          <h3 className="titulo-panel mb-4">Ediciones</h3>
          {ediciones.length === 0 ? (
            <p className="text-gray-400 text-sm">No hay ediciones creadas</p>
          ) : (
            ediciones.map((e) => (
              <button
                key={e.id}
                onClick={() => cargarInscripciones(e)}
                className={`w-full text-left p-3 rounded-xl mb-2 transition border
                  ${edicionSeleccionada?.id === e.id
                    ? "border-dorado bg-oscuro"
                    : "border-gray-700 hover:border-dorado"
                  }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <p className="text-white text-sm font-semibold">
                    {e.cursos?.nombre}
                  </p>
                  <span className={`insignia ${colorEstado[e.estado]}`}>
                    {e.estado}
                  </span>
                </div>
                <p className="text-gray-400 text-xs">
                  Inicio: {new Date(e.fecha_inicio + "T00:00:00").toLocaleDateString("es-CO")}
                </p>
                {e.miembros && (
                  <p className="text-gray-500 text-xs mt-1">
                    A cargo de: {e.miembros.nombres} {e.miembros.apellidos}
                  </p>
                )}
              </button>
            ))
          )}
        </div>

        {/* Inscripciones */}
        <div className="panel lg:col-span-2 p-4">
          {!edicionSeleccionada ? (
            <div className="flex items-center justify-center h-64">
              <p className="text-gray-400">← Selecciona una edición para ver los inscritos</p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="titulo-panel">
                    {edicionSeleccionada.cursos?.nombre}
                  </h3>
                  <p className="text-gray-400 text-sm">
                    {new Date(edicionSeleccionada.fecha_inicio + "T00:00:00").toLocaleDateString("es-CO")}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <p className="text-white font-black text-2xl">{totalCompletados}/{inscripciones.length}</p>
                    <p className="text-gray-400 text-xs">completados</p>
                  </div>
                  <button
                    onClick={() => {
                      setMiembroAInscribir("");
                      setBusquedaInscripcion("");
                      setMostrarInscribir(true);
                    }}
                    className="btn-primario btn-sm"
                  >
                    Inscribir
                  </button>
                </div>
              </div>

              {/* Cambiar estado */}
              <div className="flex gap-2 mb-4">
                {["abierto", "en_curso", "finalizado"].map(estado => (
                  <button
                    key={estado}
                    onClick={() => cambiarEstadoEdicion(edicionSeleccionada, estado)}
                    className={`px-3 py-1 rounded-lg text-xs transition border
                      ${edicionSeleccionada.estado === estado
                        ? "border-dorado text-dorado"
                        : "border-gray-600 text-gray-400 hover:border-dorado"
                      }`}
                  >
                    {estado}
                  </button>
                ))}
              </div>

              {cargando ? (
                <Esqueleto texto="Cargando inscritos…" />
              ) : inscripciones.length === 0 ? (
                <p className="text-gray-400 text-sm text-center py-8">
                  No hay miembros inscritos aún. Haz clic en "Inscribir"
                </p>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
                  {inscripciones.map((i) => (
                    <div
                      key={i.id}
                      className={`flex items-center justify-between p-3 rounded-xl border transition
                        ${i.completado ? "border-green-700 bg-oscuro" : "border-gray-700"}`}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          onClick={() => toggleCompletado(i)}
                          className={`w-6 h-6 rounded-full border-2 flex items-center justify-center cursor-pointer transition
                            ${i.completado ? "bg-green-500 border-green-500" : "border-gray-500 hover:border-dorado"}`}
                        >
                          {i.completado && <span className="text-white text-xs font-bold">✓</span>}
                        </div>
                        <div>
                          <p className="text-white text-sm font-semibold">
                            {i.miembros?.nombres} {i.miembros?.apellidos}
                          </p>
                          {i.completado && i.fecha_completado && (
                            <p className="text-green-400 text-xs">
                              Completado: {new Date(i.fecha_completado + "T00:00:00").toLocaleDateString("es-CO")}
                            </p>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => eliminarInscripcion(i.id, `${i.miembros?.nombres}`)}
                        className="text-red-400 hover:text-red-300 text-sm px-2 py-1 rounded-lg hover:bg-red-900 transition"
                      >Eliminar</button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Modal nueva edición */}
      {mostrarNuevaEdicion && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/80 p-4 backdrop-blur-sm ventana-fondo">
          <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-zinc-950 p-6 sm:p-8 ventana-panel">
            <h3 className="text-xl font-black uppercase tracking-tight text-white mb-6">Nueva Edición de Curso</h3>
            <div className="space-y-4">
              <div>
                <label className={label}>Curso *</label>
                <Selector
                  value={nuevaEdicion.curso_id}
                  onChange={e => setNuevaEdicion({ ...nuevaEdicion, curso_id: e.target.value })}
                  className={input}
                >
                  <option value="">Seleccionar...</option>
                  {cursos.map(c => (
                    <option key={c.id} value={c.id}>Nivel {c.nivel} - {c.nombre}</option>
                  ))}
                </Selector>
              </div>
              <DateInput
                label="Fecha de Inicio *"
                className={input}
                value={nuevaEdicion.fecha_inicio}
                onChange={e => setNuevaEdicion({ ...nuevaEdicion, fecha_inicio: e.target.value })}
              />
              <DateInput
                label="Fecha de Fin"
                className={input}
                value={nuevaEdicion.fecha_fin}
                onChange={e => setNuevaEdicion({ ...nuevaEdicion, fecha_fin: e.target.value })}
              />
              <div>
                <label className={label}>Notas</label>
                <textarea value={nuevaEdicion.notas}
                  onChange={e => setNuevaEdicion({ ...nuevaEdicion, notas: e.target.value })}
                  className={input} rows={2} placeholder="Notas opcionales..." />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={crearEdicion}
                className="flex-1 btn-primario">
                Crear Edición
              </button>
              <button onClick={() => setMostrarNuevaEdicion(false)}
                className="flex-1 btn-fantasma">
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal inscribir miembro */}
      {mostrarInscribir && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/80 p-4 backdrop-blur-sm ventana-fondo">
          <div className="w-full max-w-md rounded-2xl border border-white/10 bg-zinc-950 p-6 sm:p-8 ventana-panel">
            <h3 className="text-xl font-black uppercase tracking-tight text-white mb-6">Inscribir Miembro</h3>
            <div>
              <label className={label}>Buscar por nombre *</label>
              <input
                type="text"
                value={busquedaInscripcion}
                onChange={e => {
                  setBusquedaInscripcion(e.target.value);
                  setMiembroAInscribir("");
                }}
                className={input}
                placeholder="Escribe parte del nombre"
                autoFocus
              />

              {miembroSeleccionado && (
                <div className="mt-3 rounded-xl border border-dorado bg-oscuro p-3">
                  <p className="text-white text-sm font-semibold">
                    {miembroSeleccionado.nombres} {miembroSeleccionado.apellidos}
                  </p>
                  <p className="text-gray-400 text-xs">
                    {miembroSeleccionado.estado}
                  </p>
                </div>
              )}

              {!miembroSeleccionado && busquedaLimpia && (
                <div className="mt-3 max-h-72 overflow-y-auto rounded-xl border border-gray-700">
                  {miembrosDisponibles.length === 0 ? (
                    <p className="p-3 text-sm text-gray-400">
                      No hay miembros disponibles con esa búsqueda.
                    </p>
                  ) : (
                    miembrosDisponibles.map(m => (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => {
                          setMiembroAInscribir(String(m.id));
                          setBusquedaInscripcion(`${m.nombres} ${m.apellidos}`);
                        }}
                        className="w-full border-b border-gray-700 p-3 text-left transition last:border-b-0 hover:bg-oscuro"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <p className="text-white text-sm font-semibold">
                              {m.nombres} {m.apellidos}
                            </p>
                            <p className="text-gray-400 text-xs">
                              {m.estado}
                            </p>
                          </div>
                          <span className="shrink-0 rounded-full bg-oscuro px-2 py-1 text-xs text-gray-300">
                            {m.estado}
                          </span>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              )}

              {!busquedaLimpia && (
                <p className="mt-2 text-xs text-gray-500">
                  Escribe para buscar entre los miembros no inscritos.
                </p>
              )}
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={inscribirMiembro}
                disabled={!miembroAInscribir}
                className="flex-1 btn-primario">
                Inscribir
              </button>
              <button onClick={() => { setMostrarInscribir(false); setMiembroAInscribir(""); setBusquedaInscripcion(""); }}
                className="flex-1 btn-fantasma">
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default Cursos;
